import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Steps,
  Switch,
  Tag,
  Typography,
} from 'antd'
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { supabase, type TablesInsert } from '@/lib/supabase'
import { useIsMobile } from '@/lib/hooks'
import {
  BangumiSubjectCard,
  useBangumiSubject,
} from '@/components/BangumiSubjectCard'
import { BangumiSearchPanel } from '@/components/BangumiSearchPanel'
import { TranslateFieldButton } from '@/components/TranslateFieldButton'
import type { BangumiSearchHit } from '@/lib/bangumi'
import { fetchDandanAnimeId, isDandanConfigured } from '@/lib/dandan'
import { computeAnimePinyin, type AnimePinyinTriple } from '@/lib/pinyin'
import {
  deriveFromBangumi,
  weekdayLabel,
  type DerivedAnimeFields,
} from '@/lib/bangumi-mapping'

export const Route = createFileRoute('/_authed/anime-new')({
  component: NewAnimePage,
})

interface WizardData {
  title: string
  bangumi_id: number | null
  type_id: number | null
  remarks: string
  is_published: boolean
  nsfw: boolean
  is_recommended: boolean
}

/** admin.create_anime RPC 的结构化返回 */
type RpcResult =
  | { ok: true; id: number }
  | { ok: false; error: 'duplicate_bangumi_id'; existing_id: number }
  | { ok: false; error: 'forbidden' }
  | { ok: false; error: 'invalid'; field: string }

const INITIAL: WizardData = {
  title: '',
  bangumi_id: null,
  type_id: null,
  remarks: '',
  is_published: false,
  nsfw: false,
  is_recommended: false,
}

const DANDAN_CONFIGURED = isDandanConfigured()

const STEPS = [
  { title: '基础信息', description: '名称 + Bangumi' },
  { title: '字段映射', description: '从 Bangumi 派生' },
  { title: '上架配置', description: '发布 / NSFW / 推荐' },
  { title: '检查提交', description: '确认无误后插入' },
] as const

function NewAnimePage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()

  const [step, setStep] = useState(0)
  const [data, setData] = useState<WizardData>(INITIAL)

  const bangumiQuery = useBangumiSubject(data.bangumi_id)

  // 唯一性：库内 animes.bangumi_id 是否已存在
  const conflictQuery = useQuery({
    queryKey: ['animes', 'bangumi-conflict', data.bangumi_id],
    enabled: !!data.bangumi_id && data.bangumi_id > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from('animes')
        .select('id, title')
        .eq('bangumi_id', data.bangumi_id!)
        .maybeSingle()
      if (error) throw error
      return row
    },
  })

  // 弹弹 Play 中间件查 dandan_anime_id
  const dandanQuery = useQuery<number | null, Error>({
    queryKey: ['dandan', 'bgm-mapping', data.bangumi_id],
    enabled: DANDAN_CONFIGURED && !!data.bangumi_id && data.bangumi_id > 0,
    staleTime: 60 * 60_000,
    retry: false,
    queryFn: ({ signal }) => fetchDandanAnimeId(data.bangumi_id!, signal),
  })

  // anime_types 选项（step 3 select + step 4 反查名）
  const typesQuery = useQuery({
    queryKey: ['anime_types', 'all'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('anime_types')
        .select('id, name, parent_id')
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true })
      if (error) throw error
      return rows ?? []
    },
  })

  // 从 Bangumi 派生的字段：每次换 bangumi_id 重置，admin 可在 step 2 逐项改
  const [derived, setDerived] = useState<DerivedAnimeFields | null>(null)
  const lastSyncedId = useRef<number | null>(null)
  useEffect(() => {
    const id = bangumiQuery.data?.id ?? null
    if (id === null || id === lastSyncedId.current) return
    lastSyncedId.current = id
    const next = deriveFromBangumi(bangumiQuery.data!)
    setDerived(next)
    // 把 nsfw 同步到 wizard 的开关默认值
    setData((d) => ({ ...d, nsfw: next.nsfw }))
  }, [bangumiQuery.data])

  // 拼音三件套：从当前 title 实时计算 → pinyin_name / pinyin_full_nospace / pinyin_initials
  const pinyinTriple = useMemo(
    () => computeAnimePinyin(data.title),
    [data.title],
  )

  const insertMutation = useMutation({
    mutationFn: async (payload: TablesInsert<'animes'>) => {
      // admin schema 还没生成进 database.types.ts，先 cast 跳过类型检查
      const { data: raw, error } = await (supabase as any)
        .schema('admin')
        .rpc('create_anime', { p_payload: payload })
      if (error) throw new Error(error.message || 'RPC 调用失败')
      const result = raw as RpcResult | null
      if (!result || typeof result !== 'object') {
        throw new Error('RPC 返回结构异常')
      }
      if (!result.ok) {
        switch (result.error) {
          case 'duplicate_bangumi_id':
            throw new Error(
              `Bangumi ID 重复：库内已有 ID #${result.existing_id} 绑定 bangumi_id=${payload.bangumi_id}`,
            )
          case 'forbidden':
            throw new Error('权限不足，仅管理员可创建番剧')
          case 'invalid':
            throw new Error(`字段「${result.field}」校验失败`)
          default: {
            const err = (result as { error?: string }).error ?? 'unknown'
            throw new Error(`未知错误：${err}`)
          }
        }
      }
      return { id: result.id }
    },
    onSuccess: () => {
      message.success('番剧已创建')
      qc.invalidateQueries({ queryKey: ['animes'] })
      navigate({ to: '/anime' })
    },
    onError: (e: Error) => message.error(e.message || '创建失败'),
  })

  function submit() {
    if (!data.bangumi_id || !derived) {
      message.error('缺少 Bangumi 数据')
      return
    }
    const dandanQueried = DANDAN_CONFIGURED && dandanQuery.isSuccess
    const payload: TablesInsert<'animes'> = {
      // 用户输入 / 决策（覆盖 derived）
      title: data.title.trim(),
      bangumi_id: data.bangumi_id,
      type_id: data.type_id,
      remarks: data.remarks.trim() || null,
      is_published: data.is_published,
      is_recommended: data.is_recommended,
      nsfw: data.nsfw,
      // 前端计算（三件套：人可读、检索全拼无空格、检索首字母）
      pinyin_name: pinyinTriple.pinyin_name || null,
      pinyin_full_nospace: pinyinTriple.pinyin_full_nospace || null,
      pinyin_initials: pinyinTriple.pinyin_initials || null,
      // Bangumi 派生
      title_original: derived.title_original,
      description: derived.description,
      cover_url: derived.cover_url,
      bangumi_score: derived.bangumi_score,
      bangumi_rating_total: derived.bangumi_rating_total,
      format: derived.format,
      meta_tags: derived.meta_tags,
      release_date: derived.release_date,
      release_year: derived.release_year,
      update_weekday: derived.update_weekday,
      is_finished: derived.is_finished,
      director: derived.director,
      actors: derived.actors,
      aliases: derived.aliases,
      region: derived.region,
      total_episodes: derived.total_episodes,
      bangumi_synced_at: new Date().toISOString(),
      // dandan：查到了或确认无映射(404) → 写 synced_at；查询出错/未配置 → 留 null 让 backfill 重试
      dandan_anime_id: dandanQueried ? (dandanQuery.data ?? null) : null,
      dandan_synced_at: dandanQueried ? new Date().toISOString() : null,
    }
    insertMutation.mutate(payload)
  }

  const step1Valid =
    !!data.title.trim() &&
    !!data.bangumi_id &&
    data.bangumi_id > 0 &&
    !!bangumiQuery.data &&
    !bangumiQuery.error &&
    !bangumiQuery.isLoading &&
    !conflictQuery.data &&
    !conflictQuery.error &&
    !conflictQuery.isLoading

  // step 3（上架与补充）→ step 4 必须选好类型
  const step3Valid = data.type_id !== null

  const canProceed =
    step === 0
      ? step1Valid
      : step === 1
        ? true
        : step === 2
          ? step3Valid
          : false

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-header__title">新建番剧</h1>
          <div className="admin-page-header__meta">
            Bangumi 派生 · 类型与上架开关确认后写入 animes
          </div>
        </div>
      </div>

      <Card className="admin-new-card anime-new-card">
        <div className="admin-new-steps">
          {/* 手机：竖排 4 步会占满首屏，改为横排紧凑步骤条（只留标题） */}
          <Steps
            current={step}
            size={isMobile ? 'small' : undefined}
            responsive={!isMobile}
            titlePlacement={isMobile ? 'vertical' : undefined}
            items={STEPS.map((s) => ({
              title: s.title,
              description: isMobile ? undefined : s.description,
            }))}
          />
        </div>

        <div className="admin-new-body">
          {step === 0 && (
            <Step1
              data={data}
              setData={setData}
              bangumiData={bangumiQuery.data ?? null}
              bangumiError={bangumiQuery.error as Error | null}
              bangumiLoading={bangumiQuery.isLoading}
              conflict={conflictQuery.data ?? null}
              conflictLoading={conflictQuery.isLoading}
              onManualCreate={(title) =>
                insertMutation.mutate({
                  title: title.trim(),
                  is_published: false,
                })
              }
              manualSubmitting={insertMutation.isPending}
            />
          )}
          {step === 1 && (
            <Step2
              bangumiId={data.bangumi_id}
              derived={derived}
              setDerived={setDerived}
              originalDerived={
                bangumiQuery.data ? deriveFromBangumi(bangumiQuery.data) : null
              }
              pinyinTriple={pinyinTriple}
              dandanState={resolveDandanState(dandanQuery, DANDAN_CONFIGURED)}
            />
          )}
          {step === 2 && (
            <Step3
              data={data}
              setData={setData}
              types={typesQuery.data ?? []}
              typesLoading={typesQuery.isLoading}
            />
          )}
          {step === 3 && (
            <Step4
              data={data}
              derived={derived}
              pinyinTriple={pinyinTriple}
              dandanState={resolveDandanState(dandanQuery, DANDAN_CONFIGURED)}
              typeName={
                typesQuery.data?.find((t) => t.id === data.type_id)?.name ??
                null
              }
            />
          )}
        </div>

        <div className="admin-new-footer anime-new-footer">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate({ to: '/anime' })}
          >
            取消
          </Button>
          <Space>
            {step > 0 && (
              <Button onClick={() => setStep(step - 1)}>上一步</Button>
            )}
            {step < STEPS.length - 1 && (
              <Button
                type="primary"
                disabled={!canProceed}
                onClick={() => setStep(step + 1)}
              >
                下一步
              </Button>
            )}
            {step === STEPS.length - 1 && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={insertMutation.isPending}
                disabled={!derived}
                onClick={submit}
              >
                确认创建
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </div>
  )
}

/* ── Step 1：名字搜索 → 选 Bangumi 条目 ─────────────────────── */

function Step1({
  data,
  setData,
  conflict,
  conflictLoading,
  onManualCreate,
  manualSubmitting,
}: {
  data: WizardData
  setData: React.Dispatch<React.SetStateAction<WizardData>>
  bangumiData: ReturnType<typeof useBangumiSubject>['data'] | null
  bangumiError: Error | null
  bangumiLoading: boolean
  conflict: { id: number; title: string } | null
  conflictLoading: boolean
  onManualCreate: (title: string) => void
  manualSubmitting: boolean
}) {
  const [manualOpen, setManualOpen] = useState(false)
  const [manualTitle, setManualTitle] = useState('')

  function selectHit(hit: BangumiSearchHit) {
    const inferredTitle = hit.name_cn?.trim() || hit.name?.trim() || ''
    setData((d) => ({
      ...d,
      bangumi_id: hit.id,
      title: inferredTitle,
    }))
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <BangumiSearchPanel
        autoFocus
        placeholder="例：鬼灭之刃 / 鬼滅の刃"
        hint="按名称搜索 Bangumi（中 / 日 / 罗马字均可）"
        selectedId={data.bangumi_id}
        onPick={selectHit}
      />
      {data.bangumi_id ? (
        <div>
          <Divider style={{ margin: '8px 0' }} />
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            已选番剧
          </Typography.Title>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                标题（默认取 Bangumi 中文名，可改）
              </Typography.Text>
              <Input
                size="large"
                placeholder="中文标题"
                value={data.title}
                onChange={(e) =>
                  setData((d) => ({ ...d, title: e.target.value }))
                }
                maxLength={200}
                showCount
                status={!data.title.trim() ? 'error' : undefined}
              />
            </div>
            {conflictLoading ? (
              <Alert
                type="info"
                message="正在检查库内是否已存在同 Bangumi ID 的番剧…"
              />
            ) : conflict ? (
              <Alert
                type="error"
                showIcon
                message="该 Bangumi ID 已存在"
                description={`库内已有 ID #${conflict.id} 「${conflict.title}」绑定了 bangumi_id=${data.bangumi_id}，不能重复创建。`}
              />
            ) : null}
            <BangumiSubjectCard id={data.bangumi_id} compact />
          </Space>
        </div>
      ) : null}

      {/* 罕见场景的逃生口：番剧不在 Bangumi 上 / 无法接入派生 */}
      <Divider style={{ margin: '8px 0' }} />
      {manualOpen ? (
        <div
          style={{
            padding: '12px 16px',
            border: '1px dashed var(--ant-color-border)',
            borderRadius: 6,
          }}
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text strong>不通过 Bangumi 创建草稿</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              跳过 Bangumi 派生，仅写入标题，<b>默认为未发布</b>。其他字段稍后在
              /anime 编辑页补全。仅在 Bangumi 上没有这个条目时使用。
            </Typography.Text>
            <Input
              placeholder="标题（必填）"
              value={manualTitle}
              onChange={(e) => setManualTitle(e.target.value)}
              maxLength={200}
              showCount
              status={!manualTitle.trim() ? 'warning' : undefined}
            />
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!manualTitle.trim() || manualSubmitting}
                loading={manualSubmitting}
                onClick={() => onManualCreate(manualTitle)}
              >
                创建草稿
              </Button>
              <Button
                onClick={() => setManualOpen(false)}
                disabled={manualSubmitting}
              >
                取消
              </Button>
            </Space>
          </Space>
        </div>
      ) : (
        <Button type="link" size="small" onClick={() => setManualOpen(true)}>
          不通过 Bangumi 直接创建草稿
        </Button>
      )}
    </Space>
  )
}

/* ── dandan 查询状态的统一摘要（给 step 2 / step 4 渲染用） ── */

type DandanState =
  | { kind: 'disabled'; label: string }
  | { kind: 'idle'; label: string }
  | { kind: 'loading'; label: string }
  | { kind: 'error'; label: string; error: string }
  | { kind: 'no-mapping'; label: string }
  | { kind: 'mapped'; label: string; id: number }

function resolveDandanState(
  q: ReturnType<typeof useQuery<number | null, Error>>,
  configured: boolean,
): DandanState {
  if (!configured) {
    return { kind: 'disabled', label: '中间件未配置（跳过）' }
  }
  if (q.isLoading) return { kind: 'loading', label: '正在查询…' }
  if (q.error) {
    return {
      kind: 'error',
      label: '查询失败',
      error: (q.error as Error).message,
    }
  }
  if (q.isSuccess) {
    if (q.data === null)
      return { kind: 'no-mapping', label: '已查无映射 (404)' }
    return { kind: 'mapped', label: `dandan_anime_id = ${q.data}`, id: q.data }
  }
  return { kind: 'idle', label: '等待 bangumi_id' }
}

/* ── Step 2：派生字段编辑（默认填 Bangumi，逐项可改） ────────── */

type DerivedFormValues = Omit<DerivedAnimeFields, 'release_date'> & {
  release_date: Dayjs | null
}

const STEP2_FORMAT_OPTIONS = [
  { value: 'tv', label: 'TV' },
  { value: 'web', label: 'WEB' },
  { value: 'movie', label: '剧场版 (movie)' },
  { value: 'ova', label: 'OVA' },
  { value: 'oad', label: 'OAD' },
  { value: 'other', label: '其他 (other)' },
]

const STEP2_WEEKDAY_OPTIONS = [
  { value: 0, label: '周一' },
  { value: 1, label: '周二' },
  { value: 2, label: '周三' },
  { value: 3, label: '周四' },
  { value: 4, label: '周五' },
  { value: 5, label: '周六' },
  { value: 6, label: '周日' },
]

function derivedToForm(d: DerivedAnimeFields): DerivedFormValues {
  return { ...d, release_date: d.release_date ? dayjs(d.release_date) : null }
}

function formToDerived(v: DerivedFormValues): DerivedAnimeFields {
  return {
    ...v,
    release_date: v.release_date ? v.release_date.format('YYYY-MM-DD') : null,
  }
}

function Step2({
  bangumiId,
  derived,
  setDerived,
  originalDerived,
  pinyinTriple,
  dandanState,
}: {
  bangumiId: number | null
  derived: DerivedAnimeFields | null
  setDerived: React.Dispatch<React.SetStateAction<DerivedAnimeFields | null>>
  /** 当前 bangumi 拉到的"原始派生"，用于「重置为 Bangumi 默认」 */
  originalDerived: DerivedAnimeFields | null
  pinyinTriple: AnimePinyinTriple
  dandanState: DandanState
}) {
  const [form] = Form.useForm<DerivedFormValues>()

  // 每次 derived 由父更新（换 bangumi_id / 手动改字段）→ 同步到 form
  useEffect(() => {
    if (derived) form.setFieldsValue(derivedToForm(derived))
  }, [derived, form])

  if (!bangumiId || !derived) {
    return <Empty description="缺少 Bangumi 数据，返回上一步" />
  }

  const dirtyKeys =
    originalDerived === null
      ? []
      : (Object.keys(derived) as Array<keyof DerivedAnimeFields>).filter(
          (k) => {
            const a = derived[k]
            const b = originalDerived[k]
            if (Array.isArray(a) && Array.isArray(b)) {
              return (
                a.length !== b.length ||
                [...a].sort().some((v, i) => v !== [...b].sort()[i])
              )
            }
            return a !== b
          },
        )

  function resetAll() {
    if (!originalDerived) return
    setDerived(originalDerived)
    form.setFieldsValue(derivedToForm(originalDerived))
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="下列字段会和你在 step 3 的选项一起写入新番剧"
        description={
          <span>
            默认值取自 Bangumi 派生（映射规则见 <code>bangumi-mapping.ts</code>
            ）。可逐项调整；
            <b>pinyin_name / dandan_anime_id</b> 自动算，不可改。
          </span>
        }
        action={
          dirtyKeys.length > 0 ? (
            <Button size="small" icon={<ReloadOutlined />} onClick={resetAll}>
              重置 {dirtyKeys.length} 项
            </Button>
          ) : null
        }
      />

      <Form<DerivedFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onValuesChange={(_, all) => {
          setDerived(formToDerived(all))
        }}
      >
        <Form.Item label="原标题" name="title_original">
          <Input placeholder="可空" />
        </Form.Item>
        <Form.Item label="封面 URL" name="cover_url">
          <Input placeholder="https://..." />
        </Form.Item>
        <Form.Item
          label="简介"
          name="description"
          extra={
            <TranslateFieldButton
              name="description"
              onReplaced={() =>
                setDerived(formToDerived(form.getFieldsValue()))
              }
            />
          }
        >
          <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 12 }}
            maxLength={5000}
            showCount
          />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={12} sm={8}>
            <Form.Item label="格式" name="format">
              <Select allowClear options={STEP2_FORMAT_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item label="上映日期" name="release_date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item label="上映年份" name="release_year">
              <InputNumber min={1900} max={2100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={12} sm={8}>
            <Form.Item label="总集数" name="total_episodes">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item
              label="更新日"
              name="update_weekday"
              extra="完结时此项不生效"
            >
              <Select allowClear options={STEP2_WEEKDAY_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item
              label="已完结"
              name="is_finished"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item label="导演" name="director">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="地区" name="region">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="主演" name="actors">
          <Select mode="tags" tokenSeparators={[',', '，', '、']} />
        </Form.Item>
        <Form.Item label="别名" name="aliases">
          <Select mode="tags" tokenSeparators={[',', '，']} />
        </Form.Item>
        <Form.Item label="标签 (meta_tags)" name="meta_tags">
          <Select mode="tags" tokenSeparators={[',', '，']} />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={12} sm={8}>
            <Form.Item label="Bangumi 评分" name="bangumi_score">
              <InputNumber
                min={0}
                max={10}
                step={0.1}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item label="评分人数" name="bangumi_rating_total">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item
              label="NSFW"
              name="nsfw"
              valuePropName="checked"
              extra="step 3 还能再切"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Descriptions
        size="small"
        column={1}
        bordered
        title={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            自动派生（不可改）
          </Typography.Text>
        }
        items={[
          {
            label: 'pinyin_name',
            children: pinyinTriple.pinyin_name ? (
              <Typography.Text code style={{ fontSize: 12 }}>
                {pinyinTriple.pinyin_name}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">null</Typography.Text>
            ),
          },
          {
            label: 'pinyin_full_nospace',
            children: pinyinTriple.pinyin_full_nospace ? (
              <Typography.Text code style={{ fontSize: 12 }}>
                {pinyinTriple.pinyin_full_nospace}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">null</Typography.Text>
            ),
          },
          {
            label: 'pinyin_initials',
            children: pinyinTriple.pinyin_initials ? (
              <Typography.Text code style={{ fontSize: 12 }}>
                {pinyinTriple.pinyin_initials}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">null</Typography.Text>
            ),
          },
          {
            label: 'dandan_anime_id',
            children: <DandanCell state={dandanState} />,
          },
        ]}
      />
    </Space>
  )
}

/* ── Step 3：上架与补充 ───────────────────────────────────────── */

function Step3({
  data,
  setData,
  types,
  typesLoading,
}: {
  data: WizardData
  setData: React.Dispatch<React.SetStateAction<WizardData>>
  types: Array<{ id: number; name: string; parent_id: number | null }>
  typesLoading: boolean
}) {
  const switches: Array<{
    key: keyof Pick<WizardData, 'is_published' | 'nsfw' | 'is_recommended'>
    label: string
    desc: string
  }> = [
    {
      key: 'is_published',
      label: '立即发布',
      desc: '关闭则保存为草稿，前端不可见。',
    },
    {
      key: 'nsfw',
      label: 'NSFW',
      desc: '默认取 Bangumi 的 nsfw 标记；如有偏差可在此覆盖。',
    },
    {
      key: 'is_recommended',
      label: '加入推荐位',
      desc: '会在首页推荐流出现（具体逻辑由前端决定）。',
    },
  ]

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          <span style={{ color: 'var(--ant-color-error)', marginRight: 4 }}>
            *
          </span>
          类型
        </Typography.Text>
        <Select
          showSearch
          placeholder={
            typesLoading ? '加载中…' : '选择类型（来自 anime_types）'
          }
          style={{ width: '100%' }}
          value={data.type_id}
          onChange={(v) =>
            setData((d) => ({
              ...d,
              type_id: typeof v === 'number' ? v : null,
            }))
          }
          options={types.map((t) => ({
            value: t.id,
            label: t.name,
          }))}
          loading={typesLoading}
          status={data.type_id === null ? 'error' : undefined}
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
        {data.type_id === null ? (
          <Typography.Text
            type="danger"
            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
          >
            请选择一个类型后再继续
          </Typography.Text>
        ) : null}
      </div>

      <div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          备注（可选，仅管理员可见）
        </Typography.Text>
        <Input.TextArea
          placeholder="例：和姐妹项目对齐 / 临时下架原因 / 上游同步异常等"
          value={data.remarks}
          onChange={(e) => setData((d) => ({ ...d, remarks: e.target.value }))}
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={1000}
          showCount
        />
      </div>

      <Divider style={{ margin: '4px 0' }}>上架开关</Divider>

      {switches.map((row) => (
        <div
          key={row.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            border: '1px solid var(--ant-color-border-secondary)',
            borderRadius: 6,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{row.label}</div>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 2 }}
            >
              {row.desc}
            </Typography.Text>
          </div>
          <Switch
            checked={data[row.key]}
            onChange={(v) => setData((d) => ({ ...d, [row.key]: v }))}
          />
        </div>
      ))}
    </Space>
  )
}

/* ── Step 4：检查提交 ─────────────────────────────────────────── */

function Step4({
  data,
  derived,
  pinyinTriple,
  dandanState,
  typeName,
}: {
  data: WizardData
  derived: DerivedAnimeFields | null
  pinyinTriple: AnimePinyinTriple
  dandanState: DandanState
  typeName: string | null
}) {
  if (!derived) {
    return <Empty description="缺少 Bangumi 数据，返回上一步" />
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={5} style={{ margin: 0 }}>
        将插入 animes 一行
      </Typography.Title>

      <Descriptions
        size="small"
        column={1}
        bordered
        title={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            用户输入 / 决策
          </Typography.Text>
        }
        items={[
          { label: 'title', children: data.title },
          { label: 'bangumi_id', children: data.bangumi_id ?? '—' },
          {
            label: 'type_id',
            children:
              data.type_id !== null ? (
                <span>
                  {data.type_id}{' '}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {typeName ? `（${typeName}）` : ''}
                  </Typography.Text>
                </span>
              ) : (
                <Typography.Text type="secondary">null</Typography.Text>
              ),
          },
          {
            label: 'remarks',
            children: data.remarks.trim() ? (
              <Typography.Paragraph
                style={{
                  marginBottom: 0,
                  whiteSpace: 'pre-wrap',
                  fontSize: 13,
                }}
              >
                {data.remarks.trim()}
              </Typography.Paragraph>
            ) : (
              <Typography.Text type="secondary">null</Typography.Text>
            ),
          },
          {
            label: 'is_published',
            children: (
              <Tag color={data.is_published ? 'success' : 'default'}>
                {data.is_published ? 'true（已发布）' : 'false（草稿）'}
              </Tag>
            ),
          },
          {
            label: 'nsfw',
            children: (
              <Space size={4}>
                <Tag color={data.nsfw ? 'error' : 'default'}>
                  {String(data.nsfw)}
                </Tag>
                {data.nsfw !== derived.nsfw ? (
                  <Typography.Text type="warning" style={{ fontSize: 12 }}>
                    （覆盖 Bangumi 的 {String(derived.nsfw)}）
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            label: 'is_recommended',
            children: (
              <Tag color={data.is_recommended ? 'processing' : 'default'}>
                {String(data.is_recommended)}
              </Tag>
            ),
          },
        ]}
      />

      <Descriptions
        size="small"
        column={1}
        bordered
        title={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            计算 / 外部源
          </Typography.Text>
        }
        items={[
          {
            label: 'pinyin_name',
            children: pinyinTriple.pinyin_name ? (
              <Typography.Text code style={{ fontSize: 12 }}>
                {pinyinTriple.pinyin_name}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">null</Typography.Text>
            ),
          },
          {
            label: 'pinyin_full_nospace',
            children: pinyinTriple.pinyin_full_nospace ? (
              <Typography.Text code style={{ fontSize: 12 }}>
                {pinyinTriple.pinyin_full_nospace}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">null</Typography.Text>
            ),
          },
          {
            label: 'pinyin_initials',
            children: pinyinTriple.pinyin_initials ? (
              <Typography.Text code style={{ fontSize: 12 }}>
                {pinyinTriple.pinyin_initials}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">null</Typography.Text>
            ),
          },
          {
            label: 'dandan_anime_id',
            children: <DandanCell state={dandanState} />,
          },
          {
            label: 'dandan_synced_at',
            children:
              dandanState.kind === 'mapped' ||
              dandanState.kind === 'no-mapping' ? (
                <Typography.Text code style={{ fontSize: 12 }}>
                  now()
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">
                  null（让 backfill 重试）
                </Typography.Text>
              ),
          },
        ]}
      />

      <Descriptions
        size="small"
        column={1}
        bordered
        title={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Bangumi 派生（自动）
          </Typography.Text>
        }
        items={[
          {
            label: 'title_original',
            children: <NullableStr v={derived.title_original} />,
          },
          {
            label: 'description',
            children: <NullableStr v={derived.description} ellipsis />,
          },
          {
            label: 'cover_url',
            children: <NullableStr v={derived.cover_url} mono />,
          },
          { label: 'bangumi_score', children: derived.bangumi_score ?? '—' },
          {
            label: 'bangumi_rating_total',
            children: derived.bangumi_rating_total ?? '—',
          },
          { label: 'format', children: <NullableStr v={derived.format} /> },
          {
            label: 'meta_tags',
            children: derived.meta_tags?.length ? (
              <Space wrap size={[4, 4]}>
                {derived.meta_tags.map((t, i) => (
                  <Tag key={i} style={{ margin: 0 }}>
                    {t}
                  </Tag>
                ))}
              </Space>
            ) : (
              <NullableStr v={null} />
            ),
          },
          {
            label: 'release_date',
            children: <NullableStr v={derived.release_date} />,
          },
          { label: 'release_year', children: derived.release_year ?? '—' },
          {
            label: 'update_weekday',
            children:
              derived.update_weekday !== null
                ? `${derived.update_weekday}（${weekdayLabel(derived.update_weekday)}）`
                : '—',
          },
          {
            label: 'is_finished',
            children: (
              <Tag color={derived.is_finished ? 'success' : 'default'}>
                {String(derived.is_finished)}
              </Tag>
            ),
          },
          { label: 'director', children: <NullableStr v={derived.director} /> },
          {
            label: 'actors',
            children: derived.actors?.length ? derived.actors.join('、') : '—',
          },
          {
            label: 'aliases',
            children: derived.aliases?.length ? (
              <Space wrap size={[4, 4]}>
                {derived.aliases.map((a, i) => (
                  <Tag key={i} style={{ margin: 0 }}>
                    {a}
                  </Tag>
                ))}
              </Space>
            ) : (
              <NullableStr v={null} />
            ),
          },
          { label: 'region', children: <NullableStr v={derived.region} /> },
          { label: 'total_episodes', children: derived.total_episodes ?? '—' },
          {
            label: 'bangumi_synced_at',
            children: (
              <Typography.Text code style={{ fontSize: 12 }}>
                now()
              </Typography.Text>
            ),
          },
        ]}
      />
    </Space>
  )
}

function DandanCell({ state }: { state: DandanState }) {
  switch (state.kind) {
    case 'mapped':
      return <Tag color="success">{state.id}</Tag>
    case 'no-mapping':
      return (
        <Space size={4}>
          <Typography.Text type="secondary">null</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            （{state.label}）
          </Typography.Text>
        </Space>
      )
    case 'loading':
      return <Typography.Text type="secondary">{state.label}</Typography.Text>
    case 'error':
      return (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>
          {state.label}：{state.error}
        </Typography.Text>
      )
    case 'disabled':
    case 'idle':
      return (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {state.label}
        </Typography.Text>
      )
  }
}

function NullableStr({
  v,
  mono = false,
  ellipsis = false,
}: {
  v: string | null
  mono?: boolean
  ellipsis?: boolean
}) {
  if (v === null) {
    return <Typography.Text type="secondary">null</Typography.Text>
  }
  if (ellipsis) {
    return (
      <Typography.Paragraph
        ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}
        style={{ marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}
      >
        {v}
      </Typography.Paragraph>
    )
  }
  if (mono) {
    return (
      <Typography.Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
        {v}
      </Typography.Text>
    )
  }
  return <span>{v}</span>
}
